/* eslint-disable react-hooks/rules-of-hooks */
import { Button, Tooltip } from '@bfe/moly';
import { CircleIcon, CopyIcon } from '@bfe/moly-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { Dummy } from '@web3/components';
import { invoke, replacePath, shortenAddress, web3Navigate } from '@web3/utils';
import clsx from 'clsx';
import React, { ReactNode, useState } from 'react';
import { useMeasure } from 'react-use';
import { usePopoverState } from '../../../hooks';
import { useLocale } from '../../../store/i18nMessage';
import { useCurrentWallet } from '../../../store/userWallet';
import { WalletTypesNumber } from '../../../types';
import { thirdPartyWallet } from '../../../wallet-hub/Web3WalletHubCenter';
import { bybitExtension, hasBybitExtension } from '../../../wallet-hub/hubs/BybitExtensionHub';
import { bybitIconRound, iconCaretDown, walletIcon } from '../../commonIcon';
import { Web3CopyCloudWalletModal } from '../Web3CopyCloudWalletModal';
import walletCntIcon from './images/walletconnect.svg';

export type DisplayCurrentWalletProps = {};

export function DisplayCurrentWallet(_props: DisplayCurrentWalletProps) {
  const { t } = useLocale();
  const { isThirdParty, switchToBybitExtension, isNone } = useCurrentWallet();
  const popoverState = usePopoverState(false, false);

  const thirdPartyMainAddress = thirdPartyWallet.wallets?.EVM?.address ?? Object.values(thirdPartyWallet.wallets ?? {})[0]?.address ?? '';
  const [showCopy, setShowCopy] = useState(false);
  const accountName = thirdPartyWallet.isBybitWalletExtension ? bybitExtension.walletInfo?.accountName : shortenAddress(thirdPartyMainAddress);

  return (
    <>
      <Popover open={popoverState.visible}>
        <PopoverContent asChild align="end" forceMount onOpenAutoFocus={(e) => e.preventDefault()}>
          {popoverState.domVisible && (
            <div
              className="web3-user-wallet-drop transition-all"
              onMouseEnter={popoverState.show}
              onMouseLeave={popoverState.hide}
              onTransitionEnd={popoverState.transitionEnd}
              style={{
                opacity: popoverState.visible ? 1 : 0,
              }}
            >
              <div className="text-base font-semibold">
                <h3>{t('wallet_mine')}</h3>
              </div>
              <ul className="mt-4 flex flex-col gap-4">
                {/* 三方钱包的展示 */}
                {!isNone &&
                  WalletItemRow({
                    tag: thirdPartyWallet.isBybitWalletExtension
                      ? invoke(() => {
                          switch (bybitExtension.walletType) {
                            case WalletTypesNumber.CLOUD:
                              return 'cloud';
                            case WalletTypesNumber.MNEMONIC_PHRASE:
                              return 'seed';
                            case WalletTypesNumber.PRIVATE_KEY:
                              return 'private';
                            default:
                              return 'third';
                          }
                        })
                      : 'third',
                    active: isThirdParty,
                    address: accountName,
                    onRowClick: () => {
                      web3Navigate(replacePath('/web3/balance'));
                    },
                    icon: invoke(() => {
                      if (thirdPartyWallet.isBybitBrand) {
                        return bybitIconRound;
                      }
                      if (thirdPartyWallet.isBrandWalletConnect) {
                        <img src={walletCntIcon} alt="WallWallet Connect Logo" />;
                      }
                      return <img src={thirdPartyWallet.activeWalletHub?.icon} alt={`${thirdPartyWallet.activeWalletHub?.name} Logo`} />;
                    }),
                    walletName: thirdPartyWallet.activeWalletHub?.name,
                    onCopyClick: (e) => {
                      e.stopPropagation();
                      setShowCopy(true);
                    },
                    onDisconnectClick: (e) => {
                      e.stopPropagation();
                      thirdPartyWallet.dispose();
                    },
                    tooltip: t('wallet_disconnect'),
                  })}

                {/* 常驻 bybit 插件展示(没有连接bybit插件的时候) */}
                {!thirdPartyWallet.isBybitWalletExtension && (
                  <li className="py-4 pl-4 pr-3 rounded bg-bds-trans-hover">
                    <div className="flex gap-1 items-center">
                      <Dummy>
                        {() => {
                          const [nameRef, { width }] = useMeasure<HTMLDivElement>();
                          const nameText = t('bybitWalletExtension');
                          const dom = (
                            <div ref={nameRef} className="text-base font-semibold text-ellipsis">
                              {nameText}
                            </div>
                          );
                          return width > 200 ? <Tooltip title={nameText}>{dom}</Tooltip> : dom;
                        }}
                      </Dummy>
                      <WalletTag type="seed" />
                    </div>
                    <div>
                      <div className="mt-[6px] rounded-full inline-flex items-center gap-1 py-0.5 px-1.5 border border-solid border-bds-green-700-normal w-auto">
                        <CircleIcon className="text-bds-green-700-normal " fontSize={16} />
                        <span className="text-xs">{t('supportJoinAirdrop')}</span>
                      </div>
                    </div>
                    <div className="text-sm text-bds-gray-t2 mt-2">{t('connectSeedPhraseWallet')}</div>
                    <div className="mt-4">
                      {!hasBybitExtension() ? (
                        <Button
                          size="x-small"
                          variant="primary"
                          className="w-full"
                          onClick={() => {
                            window.open(bybitExtension.installUrl);
                          }}
                        >
                          {t('download')}
                        </Button>
                      ) : (
                        <Button
                          size="x-small"
                          variant="primary"
                          className="w-full"
                          onClick={() => {
                            switchToBybitExtension();
                          }}
                        >
                          {!isNone ? t('switch') : t('connect')}
                        </Button>
                      )}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          )}
        </PopoverContent>
        <PopoverTrigger asChild>
          <div className="web3-user-wallet" onMouseEnter={popoverState.show} onMouseLeave={popoverState.hide}>
            <div className="size-5 svg-full">{walletIcon}</div>
            <span className="block text-nowrap">{thirdPartyWallet.activeWalletHub?.name}</span>
            <div className="size-3 svg-full carpet-icon transition-all">{iconCaretDown}</div>
          </div>
        </PopoverTrigger>
      </Popover>
      <Web3CopyCloudWalletModal visible={showCopy} onClose={() => setShowCopy(false)} />
    </>
  );
}

const iconOff = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <path d="M17.815 3.071c0-1.002-0.812-1.815-1.815-1.815s-1.815 0.812-1.815 1.815v14.29c0 1.002 0.812 1.815 1.815 1.815s1.815-0.812 1.815-1.815v-14.29zM8.223 10.406c0.668-0.747 0.605-1.894-0.142-2.562s-1.894-0.605-2.562 0.142c-2.226 2.487-3.581 5.775-3.581 9.376 0 7.767 6.296 14.063 14.063 14.063s14.063-6.296 14.063-14.063c0-3.601-1.355-6.889-3.581-9.376-0.668-0.747-1.816-0.81-2.562-0.142s-0.81 1.816-0.142 2.562c1.653 1.847 2.656 4.282 2.656 6.955 0 5.762-4.671 10.434-10.434 10.434s-10.434-4.671-10.434-10.434c0-2.673 1.003-5.108 2.656-6.955z"></path>
  </svg>
);

const WalletItemRow = (props: {
  active: boolean;
  onRowClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  onCopyClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDisconnectClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  icon: ReactNode;
  walletName: ReactNode;
  tooltip: ReactNode;
  address?: string;
  isCloud?: boolean;
  tag: TagType;
}) => {
  return (
    <li
      className={clsx(
        'flex py-4 pl-4 pr-3 rounded hover:bg-bds-trans-hover items-center group cursor-pointer',
        props.active ? 'bg-bds-trans-hover' : '',
      )}
      onClick={props.onRowClick}
    >
      <div className={clsx('rounded-full size-2 bg-[#12FD8C] ltr:mr-2 rtl:ml-2 flex-none', !props.active && 'opacity-0')} />
      <div
        className={clsx(
          'size-10 rounded-full border border-solid web3-wallet-icon svg-full flex-none',
          props.active ? 'border-bds-green-700-normal' : 'border-bds-gray-ele-border',
        )}
      >
        {props.icon}
      </div>
      <div className={clsx('ltr:ml-3 rtl:mr-3')}>
        <div className="flex items-start gap-1">
          <h6 className=" text-base font-semibold">{props.walletName}</h6>
          <WalletTag type={props.tag} className="mt-0.5" />
        </div>
        <button
          className=" bg-transparent flex items-center mt-1 text-bds-gray-t2 gap-1 p-0 hover:text-bds-gray-t1-title transition-all"
          onClick={props.onCopyClick}
          data-spm-even="SpotWeb3NavigationBarClick"
          data-spm-data={JSON.stringify({
            wallet_manage: 'Copy',
          })}
        >
          <p className="text-sm">{props.address}</p>
          <CopyIcon />
        </button>
      </div>
      <div className="h-5 ltr:ml-auto rtl:mr-auto flex-none">
        <Tooltip title={props.tooltip} placement="top">
          <button
            className="size-5 bg-transparent svg-full p-0 hover:text-bds-brand-700-normal transition-all opacity-0 group-hover:opacity-100"
            onClick={props.onDisconnectClick}
            {...(props.isCloud
              ? {
                  'data-spm-even': 'LogOut',
                }
              : {
                  'data-spm-even': 'SpotWeb3NavigationBarClick',
                  'data-spm-data': JSON.stringify({
                    wallet_manage: 'Disconnect',
                  }),
                })}
          >
            {iconOff}
          </button>
        </Tooltip>
      </div>
    </li>
  );
};

type TagType = 'cloud' | 'third' | 'seed' | 'private';

const WalletTag = ({ type, className }: { type: TagType; className?: string }) => {
  const { t } = useLocale();
  const tText = invoke(() => {
    switch (type) {
      case 'cloud':
        return t('cloud');
      case 'third':
        return t('wallet_three');
      case 'seed':
        return t('seedPhrase');
      case 'private':
        return t('privateKey');
      default:
        return '';
    }
  });
  if (!tText) {
    return null;
  }

  return (
    <div
      className={clsx(
        'text-[10px] leading-[14px] py-0.5 px-1',
        className,
        {
          'bg-[#06F281]': type === 'seed' || type === 'private',
          'bg-bds-gray-ele-border': type === 'third',
          'bg-[#237AFB]': type === 'cloud',
        },
        {
          'text-bds-static-black': type === 'seed' || type === 'private',
        },
      )}
    >
      {tText}
    </div>
  );
};
